import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import {
  isJobInProgress,
  isJobStale,
  triggerPOExtraction,
  type POExtractionJob,
} from "@/hooks/usePOExtraction";

const NO_LINE_ITEMS_RECALC_ERROR = "No existing line items found for recalculation";

interface UseRecalculatePOFundingOptions {
  purchaseOrderId: string | null;
  fileId: string | null;
  organisationId: string;
  enabled?: boolean;
}

function recalcSessionKey(purchaseOrderId: string, fileId: string): string {
  return `${purchaseOrderId}:${fileId}`;
}

async function hasActiveExtractionJob(fileId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("po_extraction_jobs")
    .select("status, started_at, created_at")
    .eq("file_id", fileId)
    .order("created_at", { ascending: false })
    .limit(5);

  if (error) {
    console.error("Failed to check in-progress extraction jobs:", error);
    return false;
  }

  return (data ?? []).some((job) => {
    const typed = job as Pick<POExtractionJob, "status" | "started_at" | "created_at">;
    return isJobInProgress(typed.status) && !isJobStale(typed as POExtractionJob);
  });
}

async function countPOLineItems(purchaseOrderId: string): Promise<number> {
  const { count, error } = await supabase
    .from("purchase_order_line_items")
    .select("id", { count: "exact", head: true })
    .eq("purchase_order_id", purchaseOrderId);

  if (error) {
    console.error("Failed to count PO line items:", error);
    return 0;
  }

  return count ?? 0;
}

async function isNoLineItemsRecalcError(err: unknown): Promise<boolean> {
  if (!err || typeof err !== "object") return false;

  const message = (err as { message?: string }).message ?? "";
  if (message.includes(NO_LINE_ITEMS_RECALC_ERROR)) return true;

  const context = (err as { context?: Response }).context;
  if (context && typeof context.json === "function") {
    try {
      const body = (await context.clone().json()) as { error?: string };
      if (body?.error?.includes(NO_LINE_ITEMS_RECALC_ERROR)) return true;
    } catch {
      // Response body not JSON or already consumed
    }
  }

  return false;
}

export function useRecalculatePOFunding({
  purchaseOrderId,
  fileId,
  organisationId,
  enabled = true,
}: UseRecalculatePOFundingOptions): { isRecalculating: boolean } {
  const queryClient = useQueryClient();
  const recalculatedRef = useRef<Set<string>>(new Set());
  const extractionRetriedRef = useRef<Set<string>>(new Set());
  const [isRecalculating, setIsRecalculating] = useState(false);

  useEffect(() => {
    if (!enabled || !purchaseOrderId || !organisationId || !fileId) return;

    const sessionKey = recalcSessionKey(purchaseOrderId, fileId);
    if (recalculatedRef.current.has(sessionKey)) return;

    let cancelled = false;

    const retryFullExtraction = async (): Promise<boolean> => {
      if (extractionRetriedRef.current.has(fileId)) {
        console.warn(
          `PO ${purchaseOrderId}: extraction already auto-retried for file ${fileId}`,
        );
        return false;
      }

      if (await hasActiveExtractionJob(fileId)) {
        console.warn(
          `PO ${purchaseOrderId}: extraction already in progress for file ${fileId}`,
        );
        return false;
      }

      extractionRetriedRef.current.add(fileId);
      console.warn(
        `PO ${purchaseOrderId}: no line items — re-running extraction from stored file`,
      );

      const result = await triggerPOExtraction(fileId, purchaseOrderId, organisationId);
      if (!cancelled) {
        queryClient.invalidateQueries({ queryKey: ["po-extraction-job", fileId] });
        queryClient.invalidateQueries({ queryKey: ["po-line-items", purchaseOrderId] });
      }

      if (!result.success) {
        console.error("PO extraction auto-retry failed:", result.error);
        return false;
      }

      return true;
    };

    (async () => {
      setIsRecalculating(true);

      try {
        const lineItemCount = await countPOLineItems(purchaseOrderId);
        if (cancelled) return;

        if (lineItemCount === 0) {
          await retryFullExtraction();
          return;
        }

        const { error } = await invokeFunction(supabase, "calculate-po-funding", {
          body: { purchaseOrderId, organisationId },
        });

        if (error) {
          throw error;
        }

        recalculatedRef.current.add(sessionKey);

        if (!cancelled) {
          queryClient.invalidateQueries({
            queryKey: ["po-line-items", purchaseOrderId],
          });
          queryClient.invalidateQueries({
            queryKey: ["available-skus", organisationId],
          });
          queryClient.invalidateQueries({
            queryKey: ["sku-mappings", organisationId],
          });
        }
      } catch (err) {
        if (await isNoLineItemsRecalcError(err)) {
          await retryFullExtraction();
          return;
        }
        console.error("PO funding recalculation failed:", err);
      } finally {
        if (!cancelled) {
          setIsRecalculating(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [purchaseOrderId, fileId, organisationId, enabled, queryClient]);

  return { isRecalculating };
}
